import { useState, useCallback, useRef } from "react";
import {
  captureMetrics,
  getDeltas,
  clearMetrics,
  type MetricSnapshot,
  type MetricDeltas,
} from "../utils/metricDeltaTracker";

interface UseMetricDeltasReturn {
  /** Current deltas to display (null when not showing or suppressed by What-If) */
  deltas: MetricDeltas | null;
  /** Capture current metrics before opening Settings */
  captureBeforeSettings: (metrics: MetricSnapshot) => void;
  /** Check for deltas after returning from Settings */
  checkForDeltas: (currentMetrics: MetricSnapshot) => void;
  /** Dismiss all deltas immediately */
  dismissDeltas: () => void;
}

/**
 * Hook that manages the metric delta lifecycle:
 * 1. Captures metrics before Settings opens
 * 2. Computes deltas when user returns to Dashboard
 * 3. Suppresses deltas when What-If scenario is active
 */
export function useMetricDeltas(
  isWhatIfActive: boolean
): UseMetricDeltasReturn {
  const [rawDeltas, setRawDeltas] = useState<MetricDeltas | null>(null);
  const checkedRef = useRef(false);

  // Suppress deltas during What-If mode without using an effect
  const deltas = isWhatIfActive ? null : rawDeltas;

  const captureBeforeSettings = useCallback((metrics: MetricSnapshot) => {
    captureMetrics(metrics);
    checkedRef.current = false;
  }, []);

  const checkForDeltas = useCallback(
    (currentMetrics: MetricSnapshot) => {
      if (checkedRef.current || isWhatIfActive) return;
      checkedRef.current = true;

      const computed = getDeltas(currentMetrics);
      if (computed) {
        setRawDeltas(computed);
        clearMetrics();
      }
    },
    [isWhatIfActive]
  );

  const dismissDeltas = useCallback(() => {
    setRawDeltas(null);
    clearMetrics();
  }, []);

  return { deltas, captureBeforeSettings, checkForDeltas, dismissDeltas };
}
